import { useEffect, useState } from 'react';
import type { HotelSearchTransport } from './hotelSearchFlow';
import type { HotelSearchDraft } from './searchModel';
import type { HotelSearchFilters } from '@bandido/shared/types/hotels/hotel-search-filters';
import type { HotelListOptions } from '@bandido/shared/types/hotels/hotel-search-results';
import { useHotelSearch } from './useHotelSearch';

const PAGE_SIZE = 20;

/** Pages only read the same accepted Search; catalogue overflow never creates work. */
export function useHotelSearchNavigation(transport: HotelSearchTransport, readAllResults = false) {
  const search = useHotelSearch(transport, readAllResults);
  const [page, setPage] = useState(0);
  const [pendingPage, setPendingPage] = useState<number | null>(null);
  const count = search.snapshot?.cards.length ?? 0;
  useEffect(() => {
    if (pendingPage !== null && count > pendingPage * PAGE_SIZE) {
      setPage(pendingPage);
      setPendingPage(null);
    } else if (!search.busy) setPendingPage(null);
    if (!search.busy) setPage((old) => Math.min(old, Math.max(0, Math.ceil(count / PAGE_SIZE) - 1)));
  }, [count, pendingPage, search.busy]);
  const resetPage = () => {
    setPage(0);
    setPendingPage(null);
  };
  return {
    ...search,
    cards: search.snapshot?.cards.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE) ?? [],
    page: page + 1,
    resultRange: {
      from: count ? page * PAGE_SIZE + 1 : 0,
      to: Math.min(count, (page + 1) * PAGE_SIZE),
      total: search.snapshot?.totalCount,
    },
    catalogLimited: typeof search.snapshot?.nextOffset === 'number',
    navigationKey: String(page),
    hasPrevious: page > 0,
    hasNext: count > (page + 1) * PAGE_SIZE || !!search.snapshot?.resultMeta?.next_cursor,
    start: (draft: HotelSearchDraft) => {
      resetPage();
      search.start(draft);
    },
    changeFilters: (filters: HotelSearchFilters) => {
      resetPage();
      search.changeFilters(filters);
    },
    changeOptions: (options: HotelListOptions) => {
      resetPage();
      search.changeOptions(options);
    },
    previous: () => {
      if (page > 0) {
        setPage(page - 1);
        setPendingPage(null);
      }
    },
    next: () => {
      if (search.busy || !search.committed || !search.snapshot) return;
      if (count > (page + 1) * PAGE_SIZE) setPage(page + 1);
      else if (search.snapshot.resultMeta?.next_cursor) {
        setPendingPage(page + 1);
        search.loadMore();
      }
    },
  };
}
